/**
 * Aura — online safety / identity-protection assistant
 *
 * Declares three web-accessible-resource groups. The main one exposes broad globs —
 * images, fonts, videos, locale JSON, CSS and JS — to every origin with no
 * use_dynamic_url, so all of those files are fetchable from any page at a fixed URL.
 * A second group of two content scripts is likewise exposed to every origin; only a
 * worker-runner script is narrowed to aurasvc.io.
 *
 * The action-icon PNGs are the cleanest signal: they load through probeImage with no
 * CORS grant, so they survive a restrictive page CSP.
 *
 * Manifest verified against version 3.11.0.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'fjbgpaheigpmkbdkdfghmkbnkpeofmhh';

export const aura: ExtensionDefinition = {
  id: ID,
  name: 'Aura',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Broad image/js/css globs exposed to every origin with no dynamic URL. The ' +
    'action-icon PNGs are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-action-icon-active',
      kind: 'web-accessible-resource',
      description:
        'Load images/action-icon/active_64.png via <img>. Exposed to every origin; ' +
        'needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/action-icon/active_64.png'),
    },
    {
      id: 'war-overlay-content-js',
      kind: 'web-accessible-resource',
      description: 'Fetch js/overlay-content.js — an overlay content script exposed to every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/overlay-content.js'),
    },
    {
      id: 'war-overlay-content-css',
      kind: 'web-accessible-resource',
      description: 'Fetch css/overlay-content/style.css — the overlay stylesheet, a third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'css/overlay-content/style.css'),
    },
  ],
};
